import { useState, useEffect } from 'react';
import { Calendar, ChevronDown, AlertCircle } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface CompactDateRangePickerProps {
    startDate: string;
    endDate: string;
    onDateChange: (start: string, end: string) => void;
}

export function CompactDateRangePicker({ startDate, endDate, onDateChange }: CompactDateRangePickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [tempFrom, setTempFrom] = useState<Date | undefined>(undefined);
    const [clickCount, setClickCount] = useState(0);

    // Helper para converter string YYYY-MM-DD para Date sem problemas de timezone
    const parseDate = (dateString: string): Date => {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    const [selectedRange, setSelectedRange] = useState<{ from: Date; to: Date }>({
        from: parseDate(startDate),
        to: parseDate(endDate)
    });

    // Atualizar selectedRange quando as props mudarem
    useEffect(() => {
        setSelectedRange({
            from: parseDate(startDate),
            to: parseDate(endDate)
        });
    }, [startDate, endDate]);

    const handleOpen = () => {
        setTempFrom(undefined);
        setClickCount(0);
        setIsOpen(true);
    };

    const handleDayClick = (day: Date) => {
        if (clickCount === 0) {
            setTempFrom(day);
            setClickCount(1);
        } else {
            const fromDate = tempFrom! <= day ? tempFrom! : day;
            const toDate = tempFrom! <= day ? day : tempFrom!;
            setSelectedRange({ from: fromDate, to: toDate });

            // Converter Date para string YYYY-MM-DD
            const formatToISO = (date: Date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const dayNum = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${dayNum}`;
            };

            onDateChange(formatToISO(fromDate), formatToISO(toDate));
            setTimeout(() => {
                setIsOpen(false);
                setTempFrom(undefined);
                setClickCount(0);
            }, 200);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={handleOpen}
                className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:border-blue-400 transition-all text-xs md:text-sm"
            >
                <Calendar className="h-3 w-3 md:h-4 md:w-4 text-gray-500" />
                <span className="font-medium text-gray-700">
                    {format(selectedRange.from, 'dd/MM/yy', { locale: ptBR })} -{' '}
                    {format(selectedRange.to, 'dd/MM/yy', { locale: ptBR })}
                </span>
                <ChevronDown
                    className={`h-3 w-3 md:h-3.5 md:w-3.5 text-gray-400 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>
            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute top-full left-0 md:left-auto md:right-0 mt-1 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3">
                        <div className="mb-2 text-xs text-gray-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {clickCount === 0 ? 'Clique na data inicial' : 'Clique na data final (pode ser a mesma)'}
                        </div>
                        <DayPicker
                            mode="range"
                            selected={
                                tempFrom
                                    ? { from: tempFrom, to: tempFrom }
                                    : { from: selectedRange.from, to: selectedRange.to }
                            }
                            onDayClick={handleDayClick}
                            locale={ptBR}
                            numberOfMonths={1}
                            className="rdp-compact"
                        />
                    </div>
                </>
            )}
            <style>{`
                .rdp-compact { --rdp-cell-size: 36px; --rdp-accent-color: #3b82f6; --rdp-background-color: #dbeafe; font-size: 13px; }
                .rdp-compact .rdp-day_selected { background-color: #3b82f6; color: white; font-weight: 600; }
                .rdp-compact .rdp-day_range_middle { background-color: #dbeafe; }
                .rdp-compact .rdp-day:hover:not(.rdp-day_selected) { background-color: #f3f4f6; }
                .rdp-compact .rdp-caption { font-size: 13px; font-weight: 600; }
            `}</style>
        </div>
    );
}